import React, { useMemo } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getLocations, getUserInfo, getUserList } from "redux/selectors";

const HistoryWrap = styled.div`
  padding: 0;

  .history-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    overflow: hidden;
  }

  .history-table thead tr {
    background: #fafafa;
  }

  .history-table th {
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }

  .history-table td {
    padding: 14px 16px;
    font-size: 13px;
    color: #374151;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
  }

  .history-table tbody tr:last-child td {
    border-bottom: none;
  }

  .history-table tbody tr:hover td {
    background: #fafafa;
  }

  .action-label {
    color: #374151;
    font-size: 13px;
  }

  .action-label .actor {
    font-weight: 600;
  }

  .date-text {
    color: #374151;
    font-size: 13px;
    white-space: nowrap;
  }

  .empty-state {
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
    padding: 32px 0;
  }
`;

const getName = (email, userList, currentUserEmail) => {
  if (!email || email.trim() === "") return null;
  if (email === currentUserEmail) return "You";
  return userList?.find((u) => u?.userid === email)?.fullname || email;
};

// Map label + actor email into a readable "Action by" string
// e.g. "KPI Issued by You" or "Appraisee Attended by John"
const buildActionText = (label, byEmail, userList, currentUserEmail) => {
  const name = getName(byEmail, userList, currentUserEmail);
  if (!name) return label;
  return { label, name };
};

const extractEvents = (record, userList, currentUserEmail, usertimezone) => {
  const events = [];

  const fields = [
    {
      key: "templatecreated",
      label: "Template Created",
      by: record.templatecreatedby,
      at: record.templatecreatedat,
    },
    {
      key: "templatedrafted",
      label: "Template Drafted",
      by: record.templatedraftedby,
      at: record.templatedraftedat,
    },
    {
      key: "kpicreated",
      label: "KPI Issued",
      by: record.kpicreatedby,
      at: record.kpicreatedat,
    },
    {
      key: "kpiupdated",
      label: "KPI Updated",
      by: record.kpiupdatedby,
      at: record.kpiupdatedat,
    },
    {
      key: "kpidrafted",
      label: "KPI Drafted",
      by: record.kpidraftedby,
      at: record.kpidraftedat,
    },
    {
      key: "kpiattended",
      label: "Appraisee Attended",
      by: record.attendedby,
      at: record.attendedat,
    },
    {
      key: "reviewdrafted",
      label: "Review Drafted",
      by: record.reviewraftedby,
      at: record.reviewdraftedat,
    },
    {
      key: "kpireviewed",
      label: "Appraiser Reviewed",
      by: record.reviewededby,
      at: record.reviewedat,
    },
  ];

  fields.forEach(({ key, label, by, at }) => {
    // ✅ Fix: safely handle null/undefined before calling .trim()
    const hasBy = by != null && String(by).trim() !== "";
    const hasAt = at != null && String(at).trim() !== "";

    if (hasBy || hasAt) {
      const actorName = hasBy ? getName(by, userList, currentUserEmail) : null;
      events.push({
        key: `${record.historyid}-${key}`,
        historyid: record.historyid,
        label,
        actorName,
        at: hasAt ? dayjs(at).tz(usertimezone) : null,
      });
    }
  });

  return events;
};

const KPIHistory = ({ historyList = [], currentUserEmail = "" }) => {
  const locationList = useSelector(getLocations);
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const usertimezone = locationList.find(
    (item) => item.location_id === "5"
  )?.usertimezone;

  const timelineEvents = useMemo(() => {
    const sorted = [...historyList].sort((a, b) => a.historyid - b.historyid);
    return sorted.flatMap((record) =>
      extractEvents(record, userList, currentUserEmail, usertimezone)
    );
  }, [historyList, userList, currentUserEmail, usertimezone]);

  return (
    <HistoryWrap>
      {timelineEvents.length === 0 ? (
        <div className="empty-state"></div>
      ) : (
        <table className="history-table">
          <thead>
            <tr>
              <th>Action by</th>
              <th>Date &amp; Time</th>
            </tr>
          </thead>
          <tbody>
            {timelineEvents.map((event) => (
              <tr key={event.key}>
                <td>
                  <span className="action-label">
                    {event.label}
                    {event.actorName && (
                      <>
                        {" by "}
                        <span className="actor">{event.actorName}</span>
                      </>
                    )}
                  </span>
                </td>
                <td>
                  <span className="date-text">
                    {event.at ? event.at.format("Do MMM YYYY; hh:mm A") : "-"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </HistoryWrap>
  );
};

export default KPIHistory;
